import type { ReactNode } from 'react'
import './Callout.css'

export type CalloutVariant = 'advice' | 'rule'

export interface CalloutProps {
  /**
   * Fourni par l'appelant, jamais généré — c'est lui qui relie la région à son
   * titre par `aria-labelledby`. Même contrat que `TextField`.
   */
  id: string
  /**
   * Ce que le texte *est*, pas une couleur ni une gravité. Porte l'étiquette,
   * le signe, la surface et la forme du lien.
   */
  variant?: CalloutVariant
  /** Une ligne, longueur libre. */
  title: string
  /** `h3` par défaut : le panneau des tarifs porte déjà le `h2` de la famille. */
  titleAs?: 'h3' | 'h4'
  /**
   * Le corps, en texte brut. Le composant pose `white-space: pre-line` et tient
   * la mesure des paragraphes : l'appelant n'a rien à découper.
   */
  children: ReactNode
  /**
   * Emplacement de composition, comme `Hero.cta` — jamais un couple
   * libellé + adresse. `<Button as="a" variant="light" href="…">` en `rule`,
   * `<FormLink as="a" href="…">` en `advice`. Absent, rien n'est rendu.
   */
  link?: ReactNode
  as?: 'aside' | 'section' | 'div'
}

/** Étiquette et signe sont écrits par le composant : l'appelant ne saisit pas d'apparence. */
const ETIQUETTES: Record<CalloutVariant, { signe: string; texte: string }> = {
  advice: { signe: '✦', texte: 'Bon à savoir' },
  rule: { signe: '§', texte: 'Règle du lieu' },
}

/**
 * Encart d'information attaché à un univers de soins : une règle du lieu ou un
 * conseil pratique, posé au-dessus des tarifs.
 *
 * Il ne se ferme pas, ne se replie pas et ne tronque pas — une règle qu'on
 * escamote n'est pas opposable, et les props qui le permettraient n'existent
 * donc pas. Le corps est plafonné par le champ qui l'alimente, jamais par
 * l'affichage.
 *
 * La bascule une / deux colonnes est une requête de **conteneur** à 620 px et
 * non de fenêtre : l'encart se replie correctement quel que soit ce qui
 * l'entoure.
 */
export function Callout({
  id,
  variant = 'advice',
  title,
  titleAs: Titre = 'h3',
  children,
  link,
  as: Balise = 'aside',
}: CalloutProps) {
  const etiquette = ETIQUETTES[variant]
  const titreId = `${id}-titre`

  return (
    <Balise className={`azoya-callout azoya-callout--${variant}`} aria-labelledby={titreId}>
      <div className="azoya-callout-grid">
        <div>
          <div className="azoya-callout-eyebrow">
            <span className="azoya-callout-sign" aria-hidden="true">
              {etiquette.signe}
            </span>
            {etiquette.texte}
          </div>
          <Titre id={titreId} className="azoya-callout-title">
            {title}
          </Titre>
        </div>
        <div>
          <p className="azoya-callout-body">{children}</p>
          {link && <div className="azoya-callout-link">{link}</div>}
        </div>
      </div>
    </Balise>
  )
}
